/**
 * Campos de aparência compartilhados entre o tema padrão do site (`site_branding`)
 * e os modelos de aparência (`appearance_templates`) — mesmo shape em ambos,
 * para reusar o mesmo formulário e o mesmo preview ao vivo nos dois contextos.
 */
export interface AppearanceFields {
  primary_color: string;
  accent_color: string;
  heading_font: string;
  body_font: string;
  heading_color: string;
  body_color: string;
  /** Tamanho base em px — aplicado de forma uniforme, como heading_color/body_color. */
  heading_font_size: number;
  body_font_size: number;
  layout_mode: 'boxed' | 'full';
  bg_color: string;
  bg_image_url: string;
  box_color: string;
  box_image_url: string;
  box_width: number;
  /** Raio dos cantos do box (layout boxed), em px. */
  box_radius: number;
  /** Espaçamento interno (padding) do container de conteúdo, em px. */
  page_padding: number;
  /** Margem vertical (acima/abaixo) do container de conteúdo, em px. */
  page_margin: number;
  /** Exibe a data de publicação abaixo do título automático quando o modelo é usado por posts. */
  show_post_date: boolean;
}

/** Largura padrão do box quando não definida (equivale a max-w-5xl = 64rem). */
export const DEFAULT_BOX_WIDTH = 1024;

export const DEFAULT_APPEARANCE: AppearanceFields = {
  primary_color: '#0A2342',
  accent_color: '#00C2FF',
  heading_font: 'Montserrat',
  body_font: 'Inter',
  heading_color: '#0f172a',
  body_color: '#475569',
  heading_font_size: 36,
  body_font_size: 18,
  layout_mode: 'full',
  bg_color: '#ffffff',
  bg_image_url: '',
  box_color: '#ffffff',
  box_image_url: '',
  box_width: DEFAULT_BOX_WIDTH,
  box_radius: 16,
  page_padding: 0,
  page_margin: 32,
  show_post_date: false,
};

/** Lista curada — evita input livre (fonte quebrada ou requisição arbitrária ao Google Fonts). */
export const FONT_OPTIONS = [
  { family: 'Montserrat', category: 'Título' },
  { family: 'Inter', category: 'Texto' },
  { family: 'Poppins', category: 'Título' },
  { family: 'Playfair Display', category: 'Serifada' },
  { family: 'Merriweather', category: 'Serifada' },
  { family: 'Roboto', category: 'Texto' },
  { family: 'Lora', category: 'Serifada' },
  { family: 'Oswald', category: 'Título' },
] as const;

const PRELOADED_FONTS = new Set(['Montserrat', 'Inter']);

/** Injeta um <link> do Google Fonts sob demanda, sem duplicar. Montserrat/Inter
 *  já são pré-carregadas em index.html. */
export function loadGoogleFont(family: string): void {
  if (!family || PRELOADED_FONTS.has(family)) return;
  const slug = family.replace(/\s+/g, '+');
  const id = `google-font-${slug}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${slug}:wght@400;600;700&display=swap`;
  document.head.appendChild(link);
}
